<i18n>
{
  "en": {
    "title": "Committed to Sustainability and Carbon Neutrality",
    "description": "Learn about Schneider Electric’s 10 sustainability commitments that ensure our entire ecosystem from our own operations to that of our suppliers, customers, and partners becomes carbon neutral by 2030.",
    "introduction": {
      "section": "Introduction",
      "title": "Sustainability at Schneider Electric",
      "sustainability": {
        "commitments": {
          "title": "10 commitments for a sustainable future",
          "para": "With the challenges of climate change and the global megatrends made clear, the next steps also become apparent: we must act; we must take responsibility; and we must begin with ourselves. At COP21 in 2015 we unveiled our 10 sustainability commitments. The goal of these pledges is to ensure that Schneider Electric’s entire ecosystem — from our own operations to that of our suppliers, customers, and partners — becomes carbon neutral by 2030. With this in mind, we have now renewed and strengthened our 10 sustainability commitments:"
        },
        "one": {
          "title": "1",
          "subtitle": "100 million metric tons",
          "para": "1. Avoid the emission of 100 million metric tons of CO<sub>2</sub> on our customers’ end, thanks to our offers (2018 – 2020)."
        },
        "two": {
          "title": "2",
          "subtitle": "80%",
          "para": "2. Use 80% renewable electricity by 2020 and 100% by 2030; and continue to drive energy efficiency toward doubling our energy productivity in our sites over 2005 – 2030 period."
        },
        "three": {
          "title": "3",
          "subtitle": "75%",
          "para": "3. Achieve 75% of our turnover under the new Green Premium™ program (2018 – 2020)."
        },
        "four": {
          "title": "4",
          "subtitle": "100,000 metric tons",
          "para": "4. Avoid 100,000 metric tons of primary resource consumption through ECOFIT,™ product recycling, and take-back programs (2018 – 2020)."
        },
        "five": {
          "title": "5",
          "subtitle": "100%",
          "para": "5. Use 100% cardboard and pallets for transport packing from recycled or certified sources by 2020 (Q4)."
        },
        "six": {
          "title": "6",
          "subtitle": "190 sites",
          "para": "6. Reach 200 sites labeled Toward Zero Waste to Landfill by 2020 (2015 – 2020) and recover 100% of our industrial waste by 2030 (2015 – 2030)."
        },
        "seven": {
          "title": "7",
          "subtitle": "3.5% per year",
          "para": "7. Reduce CO<sub>2</sub> emissions from the transport of our goods by 3.5% per year (annually since 2012)."
        },
        "eight": {
          "title": "8",
          "subtitle": "50 million",
          "para": "8. Facilitate access to lighting and communication with low-carbon solutions for 50 million underprivileged people by 2025 (2009 – 2025)."
        },
        "nine": {
          "title": "9",
          "subtitle": "eliminate SF<sub>6</sub>",
          "para": "9. Propose alternatives to the use of SF<sub>6</sub> gas by 2020 (2015 – 2020) and eliminate SF<sub>6</sub> from our products by 2025."
        },
        "ten": {
          "title": "10",
          "subtitle": "€10 billion",
          "para": "10. Invest €10 billion in innovation and R&D for sustainability in 10 years (2015 – 2025)."
        },
        "carbonNeutrality": {
          "title": "Accelerating our transition to carbon neutrality",
          "para": "Recently we announced more details on how we plan to achieve carbon neutrality. Schneider <a href='http://there100.org/schneider-electric?dm_i=6R6,5CU27,LW68ZH,KP73L,1' target='_blank'>has joined RE100</a> and EP100 — two global, collaborative initiatives of influential businesses led by The Climate Group and committed to climate action."
        },
        "table1": {
          "row1": {
            "cell1": "We will shift to 100% renewable electricity.",
            "cell2": "RE100 is uniting more than 100 influential businesses committed to 100% renewable electricity. <a href='http://there100.org/schneider-electric?dm_i=6R6,5CU27,LW68ZH,KP73L,1' target='_blank'>Schneider has committed to shift to 100% renewable electricity</a> by 2030 with an intermediary objective of 80% by 2020. We will accomplish this by both increasing on-site renewable energy projects across multiple sites around the globe — including factories, distribution centers, offices — and procuring renewable electricity from external providers."
          },
          "row2": {
            "cell1": "We will double our energy productivity.",
            "cell2": {
              "para1": "EP100 is made up of businesses that pledge to double their energy productivity. Schneider will work to double energy productivity by 2030 against a 2005 baseline. We’re setting an ambitious target to double the economic output from every unit of energy consumed.",
              "para2": "We’re also strengthening our commitment to measurable targets and transparency:",
              "listItem1": "Before 2020, Schneider, along with more than 400 other companies, will work with the Science Based Targets initiative to review our own resource decoupling strategies and trajectories against the +2 &deg;C target by 2100 and a one-planet compatible global economy.",
              "listItem2": "Schneider will provide full visibility and transparency for our sustainability targets through the quarterly publication of our Schneider Sustainability Impact statement."
            }
          }
        },
        "barometer": {
          "title": "2015 – 2017 Planet <span class='t-nowrap'>&amp; Society</span> barometer achievements",
          "cta": "See the full results",
          "one": {
            "title": "Climate",
            "stat": "10.3%",
            "body": "energy savings"
          },
          "two": {
            "title": "Climate",
            "stat": "10.3%",
            "body": "CO<sub>2</sub> savings from transportation"
          },
          "three": {
            "title": "Climate & Development (Sustainability offers)",
            "stat": "168,400 tons",
            "body": "of CO<sub>2</sub> avoided through maintenance, retrofit, and end-of-life services"
          }
        },
        "devGoals": {
          "title": "UN Sustainable Development Goals",
          "para1": "Schneider Electric aligns with the United Nation’s Sustainable Development Goals (SDGs), a universal call to action to end poverty, protect the planet, and ensure that all people enjoy peace and prosperity by 2030.",
          "para2": "We’re committed to accomplish the 17 SDGs through our core business and five sustainability megatrends: Climate, Circular Economy, Ethics, Health & Equity, and Development."
        }
      }
    }
  },
  "fr": {
    "title": "Committed to Sustainability and Carbon Neutrality",
    "description": "Learn about Schneider Electric’s 10 sustainability commitments that ensure our entire ecosystem from our own operations to that of our suppliers, customers, and partners becomes carbon neutral by 2030.",
    "introduction": {
      "section": "Introduction",
      "title": "Le Développement Durable pour Schneider Electric",
      "sustainability": {
        "commitments": {
          "title": "10 engagements pour un avenir durable",
          "para": "Maintenant que nous avons présenté les défis liés au changement climatique et aux grandes tendances mondiales, les prochaines étapes coulent de source. Nous devons agir et prendre nos responsabilités.  À l’occasion de la COP21 en 2015, nous avons dévoilé nos 10 engagements en faveur du développement durable. Notre objectif est de faire en sorte que l’intégralité de l’écosystème de Schneider Electric &mdash; qui englobe nos activités et celles de nos fournisseurs et partenaires &mdash; soit neutre en carbone d'ici à 2030. Pour ce faire, nous venons de revoir et de renforcer nos engagements."
        },
        "one": {
          "title": "1",
          "subtitle": "100 millions de tonnes",
          "para": "1. Éviter l’émission de 100 millions de tonnes de CO<sub>2</sub> chez nos clients grâce à nos offres (2018 &ndash; 2020)."
        },
        "two": {
          "title": "2",
          "subtitle": "80%",
          "para": "2. Utiliser 80 % d'électricité renouvelable d’ici 2020  et 100 % d’ici 2030 ; et poursuivre  la réduction des consommations énergétiques engagées depuis 2005 sur nos sites en doublant notre productivité énergétique (2005 &ndash; 2030)."
        },
        "three": {
          "title": "3",
          "subtitle": "75%",
          "para": "3. Réaliser 75 % de notre chiffre d’affaires dans le cadre de notre nouveau programme Green Premium™ (2018 &ndash; 2020)."
        },
        "four": {
          "title": "4",
          "subtitle": "100 000 tonnes",
          "para": "4. Éviter 100 000 tonnes de consommation de ressources primaires grâce au programme ECOFIT<sup>TM</sup>, de recyclage et de reprise des produits (2018 &ndash; 2020)."
        },
        "five": {
          "title": "5",
          "subtitle": "100%",
          "para": "5. Utiliser 100 % de cartons d’emballage et de palettes pour le transport à partir de sources recyclées ou certifiées d’ici 2020 et utiliser 100 %  de conditionnements et d’emballages à partir de sources recyclées ou certifiées d’ici 2030."
        },
        "six": {
          "title": "6",
          "subtitle": "200 sites",
          "para": "6. Atteindre 200 sites certifiés \"Vers le zéro déchet mis en décharge\" d’ici à 2020  et valoriser 100 % de nos déchets industriels d'ici à 2030."
        },
        "seven": {
          "title": "7",
          "subtitle": "3,5 % par an",
          "para": "7. Réduire nos émissions de CO<sub>2</sub> dues au transport de marchandises de 3,5 % par an (depuis 2012 annuellement)."
        },
        "eight": {
          "title": "8",
          "subtitle": "50 millions",
          "para": "8. Faciliter l’accès à l’éclairage et à la  communication pour 50 millions de personnes défavorisées d'ici à 2025, grâce à des solutions bas carbone."
        },
        "nine": {
          "title": "9",
          "subtitle": "Alternatives au SF<sub>6</sub>",
          "para": "9. Proposer des alternatives à l’utilisation du gaz SF<sub>6</sub> d’ici 2020 et ne plus l’utiliser dans nos produits d’ici 2025."
        },
        "ten": {
          "title": "10",
          "subtitle": "10 milliards €",
          "para": "10. Investir 10 milliards d’euros en 10 ans dans l’innovation et la R&D pour le développement durable (2015 &ndash; 2025)."
        },
        "carbonNeutrality": {
          "title": "Accélérer notre transition vers la neutralité carbone",
          "para": "Récemment, nous avons détaillé la manière dont nous comptions parvenir à un bilan carbone neutre. Schneider Electric a <a href='http://there100.org/schneider-electric?dm_i=6R6,5CU27,LW68ZH,KP73L,1' target='_blank'>rejoint RE100</a> et EP100, deux initiatives collaboratives mondiales menées par le <em>Climate Group</em> qui rassemble des entreprises influentes et engagées en faveur du climat."
        },
        "table1": {
          "row1": {
            "cell1": "Nous mènerons notre transition vers 100 % d’électricité renouvelable.",
            "cell2": "RE100 réunit plus d'une centaine d’entreprises influentes qui s’engagent pour 100 % d’électricité renouvelable. <a href='http://there100.org/schneider-electric?dm_i=6R6,5CU27,LW68ZH,KP73L,1' target='_blank'>Schneider Electric s’est engagé à mener sa transition vers 100 % d'électricité renouvelable d’ici à 2030</a>, avec pour objectif intermédiaire d'atteindre 80 % d'ici à 2020. Nous y parviendrons en multipliant les projets d'énergie renouvelable sur site, qui s’appliqueront à plus de 1 000 sites consommateurs d'électricité dans le monde entier, dont 200 usines."
          },
          "row2": {
            "cell1": " Nous doublerons notre productivité énergétique.",
            "cell2": {
              "para1": "L’EP100 réunit des entreprises qui s’engagent à doubler leur productivité énergétique. Schneider Electric s’attachera à doubler sa productivité énergétique d’ici à 2030, par rapport à 2005. Nous nous donnons l’objectif ambitieux de doubler le rendement économique de chaque unité d’énergie consommée.",
              "para2": "Nous renforçons également notre engagement en faveur de la transparence et d’objectifs quantifiables :",
              "listItem1": "D’ici à 2020, Schneider Electric et plus de 400 autres entreprises s’associeront à l’initiative <em>Science- based targets</em> pour définir et valider leurs objectifs de développement durable en partant de l’hypothèse que la Terre franchira le seuil de 2 °C de réchauffement climatique d’ici 2050.",
              "listItem2": "Schneider Electric fera preuve d'une transparence totale quant à l’atteinte de ses objectifs à travers la publication trimestrielle de son baromètre Schneider Sustainability Impact."
            }
          }
        },
        "barometer": {
          "title": "Résultats du baromètre Planète & Société 2015 &ndash; 2017",
          "cta": "Voir l’ensemble des résultats.",
          "one": {
            "title": "Climat",
            "stat": "10,3 %",
            "body": "d'économies d'énergie"
          },
          "two": {
            "title": "Climat",
            "stat": "10,3 %",
            "body": "d'économies de CO<sub>2</sub> dans les transports"
          },
          "three": {
            "title": "Climat et Développement (offres durables)",
            "stat": "168 400 tonnes",
            "body": "de CO<sub>2</sub> évitées avec nos services de maintenance, récupération et fin de vie "
          }
        },
        "devGoals": {
          "title": "Objectifs de Développement Durable de l’ONU",
          "para1": "Schneider Electric contribue aux Objectifs de Développement Durable (ODD), appel à agir lancé par les Nations unies pour éradiquer la pauvreté, protéger la planète et faire en sorte que tous les êtres humains vivent dans la paix et la prospérité d’ici à 2030.",
          "para2": "Le Groupe participe à l’atteinte des 17 ODD à travers ses activités et ses 5 défis clés pour le développement durable : le climat, l' économie circulaire, l' éthique, la santé et équité, ainsi que le  développement."
        }
      }
    }
  }
}
</i18n>

<template>
  <div id="sustainability-at-se">
    <Hero :loResSrc="require('../../assets/img/introduction/introduction-sustainability-at-se-hero-lowres.jpg')" :hiResSrc="require('../../assets/img/introduction/introduction-sustainability-at-se-hero.jpg')" chapter="01" :section="$t('introduction.section')" :title="$t('introduction.title')"></Hero>

    <section class="m-b-120 m-t-120">
      <FourCols>
        <div slot="above">
          <h2 class="m-b-32" v-html="$t('introduction.sustainability.commitments.title')"/>
          <p id="10commitments" v-html="$t('introduction.sustainability.commitments.para')"/>
        </div>
        <FourColsItem>
          <img v-if="$i18n.locale === 'en'" :src="require('@/assets/img/introduction/10commitments_1.jpg')" alt="">
          <img v-if="$i18n.locale === 'fr'" :src="require('@/assets/img/introduction/1_10commitments_100milliontonnes.jpg')" alt="">
          <p class="" v-html="$t('introduction.sustainability.one.para')"/>
        </FourColsItem>
        <FourColsItem>
          <img v-if="$i18n.locale === 'en'" :src="require('@/assets/img/introduction/10commitments_2.jpg')" alt="">
          <img v-if="$i18n.locale === 'fr'" :src="require('@/assets/img/introduction/2_10commitments_80percent.jpg')" alt="">
          <p class="" v-html="$t('introduction.sustainability.two.para')"/>
        </FourColsItem>
        <FourColsItem>
          <img v-if="$i18n.locale === 'en'" :src="require('@/assets/img/introduction/10commitments_3.jpg')" alt="">
          <img v-if="$i18n.locale === 'fr'" :src="require('@/assets/img/introduction/3_10commitments_75percent.jpg')" alt="">
          <p class="" v-html="$t('introduction.sustainability.three.para')"/>
        </FourColsItem>
        <FourColsItem>
          <img v-if="$i18n.locale === 'en'" :src="require('@/assets/img/introduction/10commitments_4.jpg')" alt="">
          <img v-if="$i18n.locale === 'fr'" :src="require('@/assets/img/introduction/4_10commitments_100000tonnes.jpg')" alt="">
          <p class="" v-html="$t('introduction.sustainability.four.para')"/>
        </FourColsItem>
      </FourCols>
      <FourCols :above="false">
        <FourColsItem>
          <img v-if="$i18n.locale === 'en'" :src="require('@/assets/img/introduction/10commitments_5.jpg')" alt="">
          <img v-if="$i18n.locale === 'fr'" :src="require('@/assets/img/introduction/5_10commitments_100percent.jpg')" alt="">
          <p class="" v-html="$t('introduction.sustainability.five.para')"/>
        </FourColsItem>
        <FourColsItem>
          <img v-if="$i18n.locale === 'en'" :src="require('@/assets/img/introduction/10commitments_6.jpg')" alt="">
          <img v-if="$i18n.locale === 'fr'" :src="require('@/assets/img/introduction/6_10commitments_200sites.jpg')" alt="">
          <p class="" v-html="$t('introduction.sustainability.six.para')"/>
        </FourColsItem>
        <FourColsItem>
          <img v-if="$i18n.locale === 'en'" :src="require('@/assets/img/introduction/10commitments_7.jpg')" alt="">
          <img v-if="$i18n.locale === 'fr'" :src="require('@/assets/img/introduction/7_10commitments_3.5percent.jpg')" alt="">
          <p class="" v-html="$t('introduction.sustainability.seven.para')"/>
        </FourColsItem>
        <FourColsItem>
          <img v-if="$i18n.locale === 'en'" :src="require('@/assets/img/introduction/10commitments_8.jpg')" alt="">
          <img v-if="$i18n.locale === 'fr'" :src="require('@/assets/img/introduction/8_10commitments_50millions.jpg')" alt="">
          <p class="" v-html="$t('introduction.sustainability.eight.para')"/>
        </FourColsItem>
      </FourCols>
      <FourCols :above="false">
        <FourColsItem class="last">
          <img v-if="$i18n.locale === 'en'" :src="require('@/assets/img/introduction/10commitments_9.jpg')" alt="">
          <img v-if="$i18n.locale === 'fr'" :src="require('@/assets/img/introduction/9_10commitments_SF6.jpg')" alt="">
          <p class="" v-html="$t('introduction.sustainability.nine.para')"/>
        </FourColsItem>
        <FourColsItem class="last">
          <img v-if="$i18n.locale === 'en'" :src="require('@/assets/img/introduction/10commitments_10.jpg')" alt="">
          <img v-if="$i18n.locale === 'fr'" :src="require('@/assets/img/introduction/10_10commitments_10milliards.jpg')" alt="">
          <p class="" v-html="$t('introduction.sustainability.ten.para')"/>
        </FourColsItem>
      </FourCols>
    </section>

    <section class="l-bg-white l-section-pad">
      <TxtMedia class="m-b-50">
        <h2 id="carbonneutrality" class="m-b-35" slot="main-content" v-html="$t('introduction.sustainability.carbonNeutrality.title')"/>
        <p  class="m-0" slot="main-content" v-html="$t('introduction.sustainability.carbonNeutrality.para')" />
        <img slot="sidebar" src="../../assets/img/introduction/introduction-sustainability-se-accelerating-our-transition.jpg" />
      </TxtMedia>

      <div class="l-content">
        <div class="row">
          <div class="col-xs-14 col-xs-offset-2">
            <h3 class="t-h3-grey m-b-15" v-html="$t('introduction.sustainability.table1.row1.cell1')"></h3>
            <p class="m-b-32" v-html="$t('introduction.sustainability.table1.row1.cell2')"></p>

            <h3 class="t-h3-grey m-b-15" v-html="$t('introduction.sustainability.table1.row2.cell1')"></h3>
            <p v-html="$t('introduction.sustainability.table1.row2.cell2.para1')"></p>
            <p v-html="$t('introduction.sustainability.table1.row2.cell2.para2')"></p>
            <ul>
              <li class="t-darker-grey" v-html="$t('introduction.sustainability.table1.row2.cell2.listItem1')"></li>
              <li class="t-darker-grey" v-html="$t('introduction.sustainability.table1.row2.cell2.listItem2')"></li>
            </ul>
          </div>
        </div>
      </div>
    </section>

    <section class="m-b-120 m-t-120">
      <Barometer>
        <h2 class="t-grey" slot="copy" v-html="$t('introduction.sustainability.barometer.title')"/>
        <CTA slot="copy" href="/static/PSBarometer-Q42017_results.pdf" :buttonText="$t('introduction.sustainability.barometer.cta')" target="_blank"/>
        <BarometerIndicator class="col-xs-18 col-sm-9" :color="$root.sassVars.cGreenLight" :title="$t('introduction.sustainability.barometer.one.title')" :stat="$t('introduction.sustainability.barometer.one.stat')" :body="$t('introduction.sustainability.barometer.one.body')"/>
        <BarometerIndicator class="col-xs-18 col-sm-9" :color="$root.sassVars.cGreenLight" :title="$t('introduction.sustainability.barometer.two.title')" :stat="$t('introduction.sustainability.barometer.two.stat')" :body="$t('introduction.sustainability.barometer.two.body')"/>
        <BarometerIndicator class="col-xs-18" color="#009530" :title="$t('introduction.sustainability.barometer.three.title')" :stat="$t('introduction.sustainability.barometer.three.stat')" :body="$t('introduction.sustainability.barometer.three.body')"/>
      </Barometer>
    </section>

    <hr>

    <section id="UN-Sustainabile-Development-Goals" class="m-b-120 m-t-120">
      <Txt>
        <div class="m-b-32">
          <h2 id="ungoals" class="m-b-35" v-html="$t('introduction.sustainability.devGoals.title')"/>
          <p v-html="$t('introduction.sustainability.devGoals.para1')" />
          <p v-html="$t('introduction.sustainability.devGoals.para2')" />
        </div>
        <img v-if="$i18n.locale === 'en'" src="@/assets/img/introduction/introduction-sustainability-un-sustainable.png" />
        <img v-if="$i18n.locale === 'fr'" src="@/assets/img/introduction/F_2018_SDG_Poster_with_UN_emblem.png" />
      </Txt>
    </section>

    <div class="l-content">
      <FooterNav
        nextRoute="Introduction_WorkIndex"
        prevRoute="Introduction_Welcome" />
    </div>

  </div>
</template>
<script>
import Hero from '@/components/Hero'
import Txt from '@/components/Txt'
import FourCols from '@/components/FourCols'
import FourColsItem from '@/components/FourColsItem'
import FooterNav from '@/components/FooterNav'
import TxtMedia from '@/components/TxtMedia'
import Table from '@/components/Table'
import TableRow from '@/components/TableRow'
import TableCell from '@/components/TableCell'
import Barometer from '@/components/Barometer'
import BarometerIndicator from '@/components/BarometerIndicator'
import CTA from '@/components/CTA'

export default {
  name: 'Homepage',
  metaInfo () {
    return {
      title: this.$t('title'),
      meta: [
        { name: 'description', content: this.$t('description') }
      ]
    }
  },
  mounted () {
    if (window.location.hash.substr(1).length > 0) {
      this.$nextTick(function () {
        document.getElementById(window.location.hash.substr(1)).scrollIntoView()
      })
    }
  },
  components: {
    Hero,
    Txt,
    FourCols,
    FourColsItem,
    FooterNav,
    TxtMedia,
    Table,
    TableRow,
    TableCell,
    CTA,
    Barometer,
    BarometerIndicator
  }
}
</script>

<style lang="scss" scoped>
// @import '../scss/vars';
  #sustainability-at-se {
    .four-cols__item.last {
      @media(min-width: 768px) {
        margin-bottom: 0;
      }
    }
  }
</style>



// WEBPACK FOOTER //
// src/pages/introduction/sustainability-at-schneider-electric.vue